import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser'; // Import EmailJS
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Custom hook to read window width for responsive breakpoints
function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

const AITrainerKitProgram = () => {
  const width = useWindowWidth();

  // Define basic breakpoints
  const isMobile = width <= 600;
  const isTablet = width > 600 && width < 1060;
  const isDesktop = width >= 1060;

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    institutionName: '',
    role: '',
    programRequirements: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Responsive styles: adjust paddings, fontSizes, layouts
  const styles = {
    container: {
      minHeight: '100vh',
      background: '#fff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      color: '#1a1a1a',
    },
    heroSection: {
      padding: isMobile ? '56px 8px 38px' : (isTablet ? '82px 16px 56px' : '100px 20px 80px'),
      textAlign: 'center',
      position: 'relative',
      zIndex: 2,
      background: `
        linear-gradient(135deg, rgba(99, 102, 241, 0.9) 0%, rgba(139, 92, 246, 0.8) 100%),
        url('data:image/svg+xml,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 600">
            <defs>
              <pattern id="ai-circuit" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
                <g fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="1">
                  <circle cx="40" cy="40" r="2"/>
                  <path d="M0,40 Q20,20 40,40 T80,40"/>
                  <path d="M40,0 Q60,20 40,40 Q20,60 40,80"/>
                </g>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#ai-circuit)"/>
            <g fill="rgba(255,255,255,0.03)">
              <rect x="50" y="50" width="60" height="40" rx="5"/>
              <rect x="200" y="120" width="80" height="50" rx="5"/>
              <rect x="400" y="80" width="70" height="45" rx="5"/>
              <rect x="600" y="150" width="65" height="40" rx="5"/>
              <rect x="800" y="70" width="75" height="50" rx="5"/>
              <rect x="1000" y="130" width="60" height="35" rx="5"/>
            </g>
            <g fill="none" stroke="rgba(255,255,255,0.08)" stroke-width="2">
              <path d="M110,70 L200,145 L470,102 L600,170 L875,95 L1000,147"/>
            </g>
          </svg>
        `)}')
      `,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      color: 'white'
    },
    heroContent: {
      maxWidth: isMobile ? '97vw' : '900px',
      margin: '0 auto'
    },
    badge: {
      backgroundColor: 'rgba(255,255,255,0.22)',
      border: '1px solid rgba(255,255,255,0.3)',
      borderRadius: '25px',
      padding: isMobile ? '7px 12px' : '8px 20px',
      fontSize: isMobile ? '0.97rem' : '0.9rem',
      fontWeight: 500,
      marginBottom: isMobile ? '18px' : '32px',
      backdropFilter: 'blur(13px)',
      color: 'white',
      display: 'inline-block'
    },
    mainTitle: {
      fontSize: isMobile ? '2.3rem' : (isTablet ? '3rem' : '4rem'),
      fontWeight: 800,
      lineHeight: 1.14,
      marginBottom: '18px',
      color: 'white',
      textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
    },
    subtitle: {
      fontSize: isMobile ? '1.05rem' : '1.3rem',
      color: 'rgba(255,255,255,0.93)',
      lineHeight: 1.5,
      marginBottom: isMobile ? '16px' : '40px',
      textShadow: '1px 1px 2px rgba(0,0,0,0.18)'
    },
    heroDescription: {
      fontSize: isMobile ? '0.99rem' : '1.1rem',
      color: 'rgba(255,255,255,0.88)',
      lineHeight: 1.5,
      marginBottom: isMobile ? '24px' : '40px',
      maxWidth: isMobile ? '97vw' : '700px',
      margin: '0 auto'
    },
    heroButtons: {
      display: 'flex',
      gap: '16px',
      justifyContent: 'center',
      flexWrap: 'wrap'
    },
    primaryButton: {
      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      color: '#6366f1',
      border: 'none',
      padding: isMobile ? '12px 20px' : '16px 32px',
      borderRadius: '30px',
      fontSize: isMobile ? '1rem' : '1.1rem',
      fontWeight: 600,
      cursor: 'pointer',
      textDecoration: 'none',
      transition: 'all 0.3s',
      boxShadow: '0 8px 32px rgba(255,255,255,0.26)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px'
    },

    // --- Skills Section
    skillsSection: {
      padding: isMobile ? '32px 8px' : '80px 20px',
      backgroundColor: '#fff'
    },
    sectionContent: {
      maxWidth: isMobile ? '98vw' : '1200px',
      margin: '0 auto'
    },
    sectionTitle: {
      fontSize: isMobile ? '1.35rem' : '2.5rem',
      fontWeight: 700,
      textAlign: 'center',
      marginBottom: isMobile ? '8px' : '16px',
      color: '#1e293b'
    },
    sectionDescription: {
      fontSize: isMobile ? '0.99rem' : '1.1rem',
      color: '#64748b',
      textAlign: 'center',
      marginBottom: isMobile ? '28px' : '60px',
      maxWidth: isMobile ? '92vw' : '700px',
      margin: '0 auto'
    },
    skillsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : (isTablet ? '1fr 1fr' : 'repeat(auto-fit, minmax(380px, 1fr))'),
      gap: isMobile ? '17px' : '32px'
    },
    skillCard: {
      backgroundColor: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: '20px',
      padding: isMobile ? '20px' : '32px',
      transition: 'all 0.3s',
      boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
      textAlign: 'center'
    },
    skillIcon: {
      width: '60px',
      height: '60px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? '1.3em' : '1.7em',
      margin: '0 auto 20px',
      backgroundColor: '#ede9fe',
      color: '#6366f1'
    },
    skillTitle: {
      fontSize: isMobile ? '1.05rem' : '1.3rem',
      fontWeight: 700,
      color: '#1e293b',
      marginBottom: '12px'
    },
    skillDescription: {
      fontSize: isMobile ? '0.91rem' : '0.95rem',
      color: '#64748b',
      lineHeight: 1.5,
      marginBottom: '12px'
    },
    skillFeatures: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
      textAlign: 'left'
    },
    skillFeature: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '0.86rem',
      color: '#64748b',
      marginBottom: '8px'
    },
    checkIcon: {
      color: '#22c55e',
      fontSize: '14px'
    },

    // --- Hardware Section
    hardwareSection: {
      padding: isMobile ? '32px 8px' : '80px 20px',
      backgroundColor: '#f8fafc'
    },
    hardwareGrid: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '18px' : '60px',
      alignItems: 'stretch',
      marginTop: isMobile ? '18px' : '40px'
    },
    hardwareContent: {
      flex: 1,
      order: 1
    },
    hardwareList: {
      marginBottom: '30px'
    },
    hardwareItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      marginBottom: '16px',
      padding: '13px',
      backgroundColor: '#fff',
      borderRadius: '12px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    },
    hardwareIcon: {
      color: '#6366f1',
      fontSize: isMobile ? '18px' : '20px',
      marginTop: '2px'
    },
    hardwareDetails: {
      flex: 1
    },
    hardwareTitle: {
      fontSize: isMobile ? '0.97rem' : '1rem',
      fontWeight: 600,
      color: '#1e293b',
      marginBottom: '4px'
    },
    hardwareDesc: {
      fontSize: isMobile ? '0.82rem' : '0.85rem',
      color: '#64748b',
      lineHeight: 1.4
    },
    connectivitySection: {
      flex: 1,
      order: 2
    },
    connectivityGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(150px,1fr))',
      gap: '16px'
    },
    connectivityCard: {
      backgroundColor: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: '12px',
      padding: isMobile ? '12px' : '20px',
      textAlign: 'center',
      transition: 'all 0.3s'
    },
    connectivityIcon: {
      fontSize: isMobile ? '1.4em' : '32px',
      marginBottom: '12px',
      color: '#6366f1'
    },
    connectivityTitle: {
      fontSize: isMobile ? '0.93rem' : '0.9rem',
      fontWeight: 600,
      color: '#1e293b',
      marginBottom: '2px'
    },
    connectivityDesc: {
      fontSize: isMobile ? '0.8rem' : '0.84rem',
      color: '#64748b'
    },

    // --- Workflow Section
    workflowSection: {
      padding: isMobile ? '32px 8px' : '80px 20px',
      backgroundColor: '#fff'
    },
    workflowGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : (isTablet ? '1fr 1fr' : 'repeat(auto-fit, minmax(300px,1fr))'),
      gap: isMobile ? '17px' : '40px',
      marginTop: isMobile ? '12px' : '40px'
    },
    workflowCard: {
      backgroundColor: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: '20px',
      padding: isMobile ? '20px' : '32px',
      textAlign: 'center',
      position: 'relative',
      transition: 'all 0.3s'
    },
    workflowNumber: {
      position: 'absolute',
      top: '-13px',
      left: '24px',
      backgroundColor: '#6366f1',
      color: 'white',
      width: isMobile ? '26px' : '30px',
      height: isMobile ? '26px' : '30px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.92rem',
      fontWeight: '600'
    },
    workflowIcon: {
      width: '60px',
      height: '60px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? '1.25em' : '24px',
      margin: isMobile ? '13px auto' : '20px auto',
      backgroundColor: '#ede9fe',
      color: '#6366f1'
    },
    workflowTitle: {
      fontSize: isMobile ? '1.05rem' : '1.3rem',
      fontWeight: 700,
      color: '#1e293b',
      marginBottom: '10px'
    },
    workflowDescription: {
      fontSize: isMobile ? '0.93rem' : '0.95rem',
      color: '#64748b',
      lineHeight: 1.5
    },

    // --- Booking Section
    bookingSection: {
      padding: isMobile ? '32px 6px' : '80px 20px',
      backgroundColor: '#f8fafc'
    },
    formContainer: {
      maxWidth: isMobile ? '98vw' : '600px',
      margin: '32px auto 0'
    },
    formCard: {
      backgroundColor: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: '24px',
      padding: isMobile ? '17px 7px' : '40px',
      boxShadow: '0 10px 25px rgba(0,0,0,0.10)'
    },
    formTitle: {
      fontSize: isMobile ? '1.13rem' : '1.8rem',
      fontWeight: 700,
      color: '#1e293b',
      marginBottom: isMobile ? '16px' : '32px',
      textAlign: 'center'
    },
    formRow: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '0px' : '20px',
      marginBottom: isMobile ? '0px' : '24px'
    },
    formGroup: {
      flex: 1,
      marginBottom: isMobile ? '18px' : '24px'
    },
    label: {
      display: 'block',
      fontSize: isMobile ? '0.97rem' : '0.9rem',
      fontWeight: 600,
      color: '#374151',
      marginBottom: isMobile ? '6px' : '8px'
    },
    input: {
      width: '100%',
      padding: isMobile ? '10px' : '14px 16px',
      backgroundColor: '#fff',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: isMobile ? '0.97rem' : '1rem',
      color: '#1e293b',
      outline: 'none',
      transition: 'all 0.3s',
      boxSizing: 'border-box'
    },
    textarea: {
      width: '100%',
      padding: isMobile ? '10px' : '14px 16px',
      backgroundColor: '#fff',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: isMobile ? '0.97rem' : '1rem',
      color: '#1e293b',
      outline: 'none',
      transition: 'all 0.3s',
      resize: 'vertical',
      minHeight: isMobile ? '60px' : '120px',
      fontFamily: 'inherit',
      boxSizing: 'border-box'
    },
    submitBtn: {
      width: '100%',
      padding: isMobile ? '13px' : '16px 24px',
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: isMobile ? '1.01rem' : '1.1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s',
      boxShadow: '0 8px 32px rgba(99, 102, 241, 0.26)'
    }
  };

  // Data
  const skills = [
    {
      icon: '🤖',
      title: 'Machine Learning Mastery',
      description: 'Learn algorithm development, model training, and optimization techniques.',
      features: ['Algorithm development', 'Model training', 'Optimization techniques']
    },
    {
      icon: '🌐',
      title: 'IoT & Sensor Integration',
      description: 'Master data collection, processing, and device connectivity skills.',
      features: ['Data collection', 'Processing skills', 'Device connectivity']
    },
    {
      icon: '🤖',
      title: 'Robotics & Automation',
      description: 'Build and program robotic systems with AI-driven control mechanisms.',
      features: ['Servo motors', 'Actuators', 'Robotic control systems']
    },
    {
      icon: '👁️',
      title: 'Computer Vision & NLP',
      description: 'Develop image recognition and natural language processing applications.',
      features: ['Image recognition', 'Natural language processing', 'Processing applications']
    }
  ];
  const hardwareItems = [
    {
      icon: '🔬',
      title: 'Sensor Integration',
      description: 'Temperature, humidity, motion, and environmental sensors'
    },
    {
      icon: '📱',
      title: 'IoT Device Support',
      description: 'Connect and control smart devices and sensors'
    },
    {
      icon: '🤖',
      title: 'Robotic Components',
      description: 'Servo motors, actuators, and robotic control systems'
    },
    {
      icon: '⚡',
      title: 'External Processing',
      description: 'GPU acceleration and cloud computing integration'
    }
  ];

  const connectivity = [
    { icon: '🔌', title: 'Digital I/O Ports', description: 'Multiple connections for hardware expansion' },
    { icon: '⚡', title: 'External Power Supply', description: 'Supports external AC and DC power supply for external devices.' },
    { icon: '🖥️', title: 'User-Friendly', description: 'Simple to use, just like a desktop or laptop' }
  ];

  const workflow = [
    {
      icon: '⚙️',
      title: 'Setup & Configuration',
      description: 'Quick plug-and-play setup with automatic device recognition and configuration. Connect sensors, IoT devices, and robotic components.'
    },
    {
      icon: '💡',
      title: 'Hands-On Learning',
      description: 'Interactive demonstrations showcase real-world AI applications using connected hardware. Students learn by building, programming, and experimenting.'
    },
    {
      icon: '🚀',
      title: 'Real-World Applications',
      description: 'Deploy trained models to control robotic systems, analyze sensor data, and create IoT solutions. Bridge the gap between theory and practice.'
    }
  ];

  // --- HANDLERS
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.email || !formData.institutionName || !formData.role ) {
        alert("Please fill out all required fields.");
        return;
    }
    setIsSubmitting(true);

    const serviceID = 'service_n36e8hg';
    const templateID = 'template_i9jkfri';
    const publicKey = 'VAEdCMXuUKeCOw6gb';

    const emailBody = `
      New AI Trainer Kit Program Inquiry
      ----------------------------------

      Inquirer Details:
      - Full Name: ${formData.fullName}
      - Email: ${formData.email}
      - Institution: ${formData.institutionName}
      - Role: ${formData.role}

      Program Requirements:
      ${formData.programRequirements}
    `;

    const templateParams = {
        email_subject: `AI Trainer Kit Program Inquiry from ${formData.institutionName}`,
        email_body: emailBody.trim()
    };

    emailjs.send(serviceID, templateID, templateParams, publicKey)
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        alert('Thank you for your inquiry! We have received your request and will get back to you shortly.');
        setIsSubmitting(false);
        setFormData({
            fullName: '', email: '', institutionName: '', role: '', programRequirements: ''
        });
      })
      .catch((err) => {
        console.error('FAILED...', err);
        alert('There was an error submitting your inquiry. Please try again later.');
        setIsSubmitting(false);
      });
  };

  const handleInputFocus = (e) => {
    e.target.style.border = '2px solid #6366f1';
    e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
  };
  const handleInputBlur = (e) => {
    e.target.style.border = '2px solid #e2e8f0';
    e.target.style.boxShadow = 'none';
  };
  const handleCardHover = (e) => {
    e.currentTarget.style.transform = 'translateY(-5px)';
    e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.11)';
  };
  const handleCardLeave = (e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.05)';
  };
  const handleBtnHover = (e) => {
    const tgt = e.currentTarget;
    if (tgt.disabled) return;
    tgt.style.transform = 'translateY(-2px)';
    if (tgt.style.background.includes('ffffff')) {
      tgt.style.boxShadow = '0 12px 40px rgba(255,255,255,0.4)';
    } else {
      tgt.style.boxShadow = '0 12px 40px rgba(99,102,241,0.26)';
    }
  };
  const handleBtnLeave = (e) => {
    const tgt = e.currentTarget;
    tgt.style.transform = 'translateY(0)';
    if (tgt.style.background.includes('ffffff')) {
      tgt.style.boxShadow = '0 8px 32px rgba(255,255,255,0.3)';
    } else {
      tgt.style.boxShadow = '0 8px 32px rgba(99,102,241,0.22)';
    }
  };

  return (
    <div>
      <Navbar />
      <div style={styles.container}>
        {/* Hero */}
        <div style={styles.heroSection}>
          <div style={styles.heroContent}>
            <div style={styles.badge}>End-to-End AI Learning Program</div>
            <h1 style={styles.mainTitle}>AI Trainer Kit Program</h1>
            <p style={styles.subtitle}>Comprehensive AI Training for Students & Educators</p>
            <p style={{...styles.heroDescription, padding: '2.5em 1em'}}>
              Transform your institution with our comprehensive AI training program featuring hands-on
              learning through Intel-powered purpose kits. From basic concepts to advanced applications,
              students master AI through practical experience with real hardware integration.
            </p>
            <div style={styles.heroButtons}>
              <a
                href="#booking-form"
                style={styles.primaryButton}
                onMouseEnter={handleBtnHover}
                onMouseLeave={handleBtnLeave}
                onTouchStart={handleBtnHover}
                onTouchEnd={handleBtnLeave}
              >
                📚 Explore on Training Session
              </a>
            </div>
          </div>
        </div>

        {/* Skills Section */}
        <div style={styles.skillsSection}>
          <div style={styles.sectionContent}>
            <h2 style={{...styles.sectionTitle, padding: '2em 0'}}>
              Master Multiple AI Skills Through <span style={{ color: '#6366f1'}}>Hands-On Training</span>
            </h2>
            <p style={{...styles.sectionDescription, padding: '1em 0 3em'}}>
              Our comprehensive training program enables students and educators to develop practical AI
              expertise across diverse domains and applications.
            </p>
            <div style={styles.skillsGrid}>
              {skills.map((skill, idx) => (
                <div
                  key={idx}
                  style={styles.skillCard}
                  onMouseEnter={handleCardHover}
                  onMouseLeave={handleCardLeave}
                  onTouchStart={handleCardHover}
                  onTouchEnd={handleCardLeave}
                >
                  <div style={styles.skillIcon}>{skill.icon}</div>
                  <h3 style={styles.skillTitle}>{skill.title}</h3>
                  <p style={styles.skillDescription}>{skill.description}</p>
                  <ul style={styles.skillFeatures}>
                    {skill.features.map((feature, fidx) => (
                      <li key={fidx} style={styles.skillFeature}>
                        <span style={styles.checkIcon}>✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Hardware Section */}
        <div style={styles.hardwareSection}>
          <div style={styles.sectionContent}>
            <h2 style={{...styles.sectionTitle, padding: '2em 0 0'}}>
              Extensive <span style={{ color: '#6366f1' }}>Hardware Support</span>
            </h2>
            <p style={{...styles.sectionDescription, padding: '1em 0 3em'}}>
              The AI Kit supports comprehensive hardware integration including
              sensors, IoT devices, robotic components, and external processing units for real-world AI applications.
            </p>
            <div style={styles.hardwareGrid}>
              <div style={styles.hardwareContent}>
                <div style={styles.hardwareList}>
                  {hardwareItems.map((item, idx) => (
                    <div key={idx} style={styles.hardwareItem}>
                      <span style={styles.hardwareIcon}>{item.icon}</span>
                      <div style={styles.hardwareDetails}>
                        <div style={styles.hardwareTitle}>{item.title}</div>
                        <div style={styles.hardwareDesc}>{item.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={styles.connectivitySection}>
                <h3 style={{
                  fontSize: isMobile ? '1.08rem' : '1.5rem',
                  fontWeight: 700,
                  color: '#1e293b',
                  marginBottom: '3em'
                }}>
                  Connectivity Options
                </h3>
                <div style={styles.connectivityGrid}>
                  {connectivity.map((conn, idx) => (
                    <div key={idx} style={styles.connectivityCard}>
                      <div style={styles.connectivityIcon}>{conn.icon}</div>
                      <div style={styles.connectivityTitle}>{conn.title}</div>
                      <div style={styles.connectivityDesc}>{conn.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Program Workflow Section */}
        <div style={styles.workflowSection}>
          <div style={styles.sectionContent}>
            <h2 style={{...styles.sectionTitle, padding: '2em 0 0'}}>
              How the <span style={{ color: '#6366f1' }}>Program Works</span>
            </h2>
            <p style={styles.sectionDescription}>
              Experience hands-on AI learning through practical demonstrations and skill-building exercises
            </p>
            <div style={styles.workflowGrid}>
              {workflow.map((step, idx) => (
                <div
                  key={idx}
                  style={styles.workflowCard}
                  onMouseEnter={handleCardHover}
                  onMouseLeave={handleCardLeave}
                  onTouchStart={handleCardHover}
                  onTouchEnd={handleCardLeave}
                >
                  <div style={styles.workflowNumber}>{idx + 1}</div>
                  <div style={styles.workflowIcon}>{step.icon}</div>
                  <h3 style={styles.workflowTitle}>{step.title}</h3>
                  <p style={styles.workflowDescription}>{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Booking Section */}
        <div id="booking-form" style={styles.bookingSection}>
          <div style={styles.sectionContent}>
            <h2 style={styles.sectionTitle}>
              Book Your <span style={{ color: '#6366f1' }}>Training Session Today</span>
            </h2>
            <p style={{...styles.sectionDescription, padding: '3em 0'}}>
              Experience our end-to-end AI learning program. Book a demo session or full training program for your students and educators.
            </p>
            <div style={styles.formContainer}>
              <div style={styles.formCard}>
                <h3 style={styles.formTitle}>Book Demo & Training Sessions</h3>
                <form onSubmit={handleSubmit} autoComplete="off">
                  <div style={styles.formRow}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Full Name *</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        style={styles.input}
                        placeholder="Your full name"
                        required
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        style={styles.input}
                        placeholder="your.email@institution.edu"
                        required
                      />
                    </div>
                  </div>
                  <div style={styles.formRow}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Institution Name *</label>
                      <input
                        type="text"
                        name="institutionName"
                        value={formData.institutionName}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        style={styles.input}
                        placeholder="Your institution name"
                        required
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Your Role *</label>
                      <input
                        type="text"
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        style={styles.input}
                        placeholder="e.g., Educator, Administrator"
                        required
                      />
                    </div>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Program Requirements</label>
                    <textarea
                      name="programRequirements"
                      value={formData.programRequirements}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      style={styles.textarea}
                      placeholder="Tell us about your AI education needs, number of students, and specific requirements..."
                    />
                  </div>
                  <button
                    type="submit"
                    style={styles.submitBtn}
                    onMouseEnter={handleBtnHover}
                    onMouseLeave={handleBtnLeave}
                    onTouchStart={handleBtnHover}
                    onTouchEnd={handleBtnLeave}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Program Inquiry'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AITrainerKitProgram;
